import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useLocation } from 'react-router-dom';
import { StepsIndicator, StepStatus } from '@sdir/sds';
import { GetApplicationsQueryResult } from '@sdir/httpclient/lib/clients/aps/application';
import { getCurrentSteps, MssStep } from '../../../helpers/Steps';
import { ApplicationLayout } from '../ApplicationLayout';
import {
  useCurrentApplication,
  useCurrentUserInfo,
  useGetCertificateName,
} from '../../../services/hooks';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { routes } from '../../../constants/routes';

export interface FormPageLayoutProps {
  subHeading?: string | any;
  heading?: React.ReactElement | string;
  sideContent?: any;
  children: any;
  exitLabel?: string;
  mainContentMaxWidth?: string;
  hideNavigation?: boolean;
  hideTimeline?: boolean;
  narrowSide?: boolean;
  navBackLabel?: string;
  navNextLabel?: string;
  canNavBack?: boolean;
  canNavNext?: boolean;
  hideHeading?: boolean;
  hideTopHeading?: boolean;
  onExitClick?: () => void;
  onNavNextClick?: () => void;
  onNavBackClick?: () => void;
}

const NewApplicationLayout: React.FC<FormPageLayoutProps> = ({
  subHeading,
  heading,
  children,
  sideContent,
  exitLabel,
  navBackLabel,
  navNextLabel,
  canNavNext,
  canNavBack,
  hideTopHeading = false,
  hideNavigation = false,
  hideTimeline = false,
  narrowSide,
  mainContentMaxWidth,
  onExitClick,
  onNavBackClick,
  onNavNextClick,
  hideHeading = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const { application } = useCurrentApplication();

  const certificateName = useGetCertificateName(application.result?.certificate || '');
  const layoutHeading = heading || certificateName;
  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');

  const onExit = useMemo(() => {
    if (onExitClick) return onExitClick;
    if (isSid) return () => navigate(routes.sidDashboard);
    return undefined;
  }, [onExitClick, navigate]);

  return (
    <ApplicationLayout
      topHeading={intl.formatMessage({ id: 'select.certificate.h1' })}
      heading={layoutHeading}
      hideHeading={hideHeading}
      hideTopHeading={hideTopHeading}
      subHeading={subHeading}
      sideContent={sideContent}
      exitLabel={exitLabel}
      navBackLabel={navBackLabel}
      navNextLabel={navNextLabel}
      canNavNext={canNavNext}
      canNavBack={canNavBack}
      hideNavigation={hideNavigation}
      narrowSide={narrowSide}
      mainContentMaxWidth={mainContentMaxWidth}
      onExitClick={onExit}
      onNavBackClick={onNavBackClick}
      onNavNextClick={onNavNextClick}
      timeline={
        application.result && !hideTimeline ? (
          <NewApplicationSteps application={application.result} />
        ) : null
      }
    >
      {children}
    </ApplicationLayout>
  );
};

export default NewApplicationLayout;

const NewApplicationSteps: FC<{ application: GetApplicationsQueryResult }> = ({ application }) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const location = useLocation();
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  const stepsForPathname = useMemo(() => {
    if (application.applicationType) {
      return getCurrentSteps(location.pathname, application.applicationType, intl, isExternal);
    }
    return [];
  }, [location.pathname, application.applicationType, intl]);

  const handleStepClick = useCallback(
    (step: MssStep) => {
      if (step.status !== StepStatus.DISABLED) {
        navigate(generatePath(step.route, { applicationUid: application.uid || '' }));
      }
    },
    [application.uid, navigate]
  );

  return <StepsIndicator steps={stepsForPathname} displayValue={false} onClick={handleStepClick} />;
};
